import React, { useState, useEffect } from 'react';
import { ProcessoComercial } from '../ComercialDashboard';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { ContratoViewer } from './ContratoViewer';
import { 
  X, 
  Users, 
  FileText, 
  History, 
  Paperclip,
  Calendar,
  User,
  MapPin,
  Phone,
  Mail,
  CreditCard,
  FileCheck,
  AlertCircle,
  CheckCircle,
  Plus,
  Download,
  Trash2,
  Edit,
  Send,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ParteForm from './ParteForm';
import AnexoUpload from './AnexoUpload';
import ContratoGenerator from './ContratoGenerator';
import PublicFormLinks from './PublicFormLinks';
import { ParteDetailModal } from './ParteDetailModal';

interface ProcessoDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  processo: ProcessoComercial;
}

interface Parte {
  id: string;
  processo_id: string;
  tipo_parte: string;
  nome_completo: string;
  cpf_cnpj?: string | null;
  email?: string | null;
  telefone?: string | null;
  endereco?: any;
  dados_bancarios?: any;
  dados_pessoais?: any;
  dados_conjuge?: any;
  dados_imovel?: any;
  documentos?: any[];
  status_documentos: string;
  created_at: string;
}

interface Contrato {
  id: string;
  versao: number | null;
  status: string;
  conteudo_html?: string | null;
  pdf_url?: string | null;
  comentarios?: any[];
  aprovacoes?: any;
  created_at: string;
}

interface Anexo {
  id: string;
  tipo: string;
  nome_arquivo: string;
  url_arquivo: string;
  tamanho_bytes?: number | null;
  uploaded_by?: string | null;
  created_at: string;
}

interface Historico {
  id: string;
  acao: string;
  descricao?: string | null;
  usuario_id?: string | null;
  metadata?: any;
  created_at: string;
}

export const ProcessoDetailModal: React.FC<ProcessoDetailModalProps> = ({
  isOpen,
  onClose,
  processo
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'partes' | 'contratos' | 'historico' | 'anexos'>('partes');
  const [partes, setPartes] = useState<Parte[]>([]);
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [historico, setHistorico] = useState<Historico[]>([]);
  const [loading, setLoading] = useState(true);
  const [isParteFormOpen, setIsParteFormOpen] = useState(false);
  const [isAnexoUploadOpen, setIsAnexoUploadOpen] = useState(false);
  const [isContratoGeneratorOpen, setIsContratoGeneratorOpen] = useState(false);
  const [isPublicLinkModalOpen, setIsPublicLinkModalOpen] = useState(false);
  const [selectedContratoId, setSelectedContratoId] = useState<string | null>(null);
  const [isContratoViewerOpen, setIsContratoViewerOpen] = useState(false);
  const [viewingParte, setViewingParte] = useState<Parte | null>(null);
  const [isParteDetailOpen, setIsParteDetailOpen] = useState(false);
  const [editingParte, setEditingParte] = useState<Parte | null>(null);

  useEffect(() => {
    if (isOpen && processo?.id) {
      loadData();
    }
  }, [isOpen, processo?.id]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load partes
      const { data: partesData } = await supabase
        .from('partes_processo')
        .select('*')
        .eq('processo_id', processo.id)
        .order('created_at');

      // Load contratos
      const { data: contratosData } = await supabase
        .from('contratos_gerados')
        .select('*')
        .eq('processo_id', processo.id)
        .order('versao', { ascending: false });

      // Load anexos
      const { data: anexosData } = await supabase
        .from('anexos_processo')
        .select('*')
        .eq('processo_id', processo.id)
        .order('created_at', { ascending: false });

      // Load historico
      const { data: historicoData } = await supabase
        .from('historico_processo')
        .select('*')
        .eq('processo_id', processo.id)
        .order('created_at', { ascending: false });

      setPartes((partesData || []) as Parte[]);
      setContratos((contratosData || []) as Contrato[]);
      setAnexos((anexosData || []) as Anexo[]);
      setHistorico((historicoData || []) as Historico[]);

    } catch (error) {
      console.error('Erro ao carregar dados do processo:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados do processo",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'criado':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'coletando_dados':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'contrato_rascunho':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'revisao':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'aprovado':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'finalizado':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelado':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDocumentStatusIcon = (status: string) => {
    switch (status) {
      case 'completo':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'incompleto':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getTipoParteLabel = (tipo: string) => {
    const labels = {
      comprador: 'Comprador',
      vendedor: 'Vendedor',
      locatario: 'Locatário',
      locador: 'Locador',
      fiador: 'Fiador'
    };
    return labels[tipo as keyof typeof labels] || tipo;
  };

  const handleAddParte = () => {
    setEditingParte(null);
    setIsParteFormOpen(true);
  };

  const handleEditParte = (e: React.MouseEvent, parte: Parte) => {
    e.stopPropagation();
    setEditingParte(parte);
    setIsParteFormOpen(true);
  };

  const handleViewParte = (parte: Parte) => {
    setViewingParte(parte);
    setIsParteDetailOpen(true);
  };

  const handleParteFormClose = () => {
    setIsParteFormOpen(false);
    setEditingParte(null);
    loadData();
  };

  const handleAddAnexo = () => {
    setIsAnexoUploadOpen(true);
  };

  const handleAnexoUploadClose = () => {
    setIsAnexoUploadOpen(false);
    loadData();
  };

  const handleDownloadAnexo = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGenerateContrato = () => {
    setIsContratoGeneratorOpen(true);
  };

  const handleContratoGenerated = () => {
    setIsContratoGeneratorOpen(false);
    loadData();
  };

  const handleShowPublicLinks = () => {
    setIsPublicLinkModalOpen(!isPublicLinkModalOpen);
  };

  const handleDeleteParte = async (parteId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta parte?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('partes_processo')
        .delete()
        .eq('id', parteId);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Parte excluída com sucesso",
      });

      loadData();
    } catch (error: any) {
      console.error('Erro ao excluir parte:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao excluir parte",
        variant: "destructive",
      });
    }
  };

  const handleResendLink = async (parte: Parte) => {
    if (!parte.email) {
      toast({
        title: "Erro",
        description: "Esta parte não possui email cadastrado",
        variant: "destructive",
      });
      return;
    }

    try {
      // Buscar ou criar token para esta parte
      let tokenData = await supabase
        .from('tokens_formulario_publico')
        .select('token')
        .eq('processo_id', processo.id)
        .eq('tipo_parte', parte.tipo_parte)
        .eq('status', 'pendente')
        .maybeSingle();

      let token = tokenData.data?.token;

      // Se não existe token, criar um novo
      if (!token) {
        token = crypto.randomUUID();
        
        // Obter ID do usuário atual para auditoria
        const { data: { user } } = await supabase.auth.getUser();
        
        const { error: tokenError } = await supabase
          .from('tokens_formulario_publico')
          .insert({
            processo_id: processo.id,
            tipo_parte: parte.tipo_parte,
            token: token,
            status: 'pendente',
            created_by: user?.id,
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          });

        if (tokenError) throw tokenError;
      }

      // Enviar email
      const { error: emailError } = await supabase.functions.invoke('send-form-link-email', {
        body: {
          destinatario_email: parte.email,
          destinatario_nome: parte.nome_completo,
          link_formulario: `${window.location.origin}/formulario/${token}`,
          processo_titulo: processo.titulo,
          tipo_parte: parte.tipo_parte,
          validade: '7 dias'
        }
      });

      if (emailError) throw emailError;

      toast({
        title: "Sucesso",
        description: `Link reenviado para ${parte.email}`,
      });

    } catch (error: any) {
      console.error('Erro ao reenviar link:', error);
      toast({
        title: "Erro ao reenviar link",
        description: error.message || "Não foi possível reenviar o link",
        variant: "destructive",
      });
    }
  };

  const handleDeleteProcesso = async () => {
    if (!confirm('Tem certeza que deseja excluir este processo? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('processos_comerciais')
        .delete()
        .eq('id', processo.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Processo excluído com sucesso",
      });

      onClose();
    } catch (error: any) {
      console.error('Erro ao excluir processo:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao excluir processo",
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-foreground">{processo.titulo}</h2>
            <div className="flex items-center gap-4 mt-2">
              <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(processo.status)}`}>
                {processo.status}
              </div>
              <span className="text-sm text-muted-foreground">
                Criado em {format(new Date(processo.created_at), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDeleteProcesso}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Process Info */}
        <div className="p-6 border-b border-border bg-muted/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{processo.unidade || 'Unidade não informada'}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                {processo.responsavel?.first_name} {processo.responsavel?.last_name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Início: {format(new Date(processo.created_at), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            </div>
          </div>
          {processo.observacoes && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">{processo.observacoes}</p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex space-x-1 p-4">
            <Button
              variant={activeTab === 'partes' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('partes')}
            >
              <Users className="h-4 w-4 mr-2" />
              Partes ({partes.length})
            </Button>
            <Button
              variant={activeTab === 'contratos' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('contratos')}
            >
              <FileText className="h-4 w-4 mr-2" />
              Contratos ({contratos.length})
            </Button>
            <Button
              variant={activeTab === 'anexos' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('anexos')}
            >
              <Paperclip className="h-4 w-4 mr-2" />
              Anexos ({anexos.length})
            </Button>
            <Button
              variant={activeTab === 'historico' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('historico')}
            >
              <History className="h-4 w-4 mr-2" />
              Histórico ({historico.length})
            </Button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 280px)' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Partes Tab */}
              {activeTab === 'partes' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-foreground">Partes Envolvidas</h3>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={handleShowPublicLinks}>
                        Ver Links Enviados
                      </Button>
                      <Button size="sm" onClick={handleAddParte}>
                        <Plus className="h-4 w-4 mr-2" />
                        Enviar Link para Parte
                      </Button>
                    </div>
                  </div>

                  {isPublicLinkModalOpen && (
                    <div className="mb-4 p-4 border rounded-lg bg-muted/50">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Links Públicos Gerados</h4>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setIsPublicLinkModalOpen(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <PublicFormLinks processoId={processo.id} />
                    </div>
                  )}

                  {partes.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Nenhuma parte cadastrada</p>
                    </div>
                  ) : (
                     <div className="grid gap-4">
                      {partes.map((parte) => (
                        <div 
                          key={parte.id} 
                          className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleViewParte(parte)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-medium text-foreground">{parte.nome_completo}</h4>
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                  {getTipoParteLabel(parte.tipo_parte)}
                                </span>
                                <div className="flex items-center gap-1">
                                  {getDocumentStatusIcon(parte.status_documentos)}
                                  <span className="text-xs text-muted-foreground">
                                    {parte.status_documentos}
                                  </span>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                                {parte.cpf_cnpj && (
                                  <div className="flex items-center gap-2">
                                    <CreditCard className="h-4 w-4" />
                                    {parte.cpf_cnpj}
                                  </div>
                                )}
                                {parte.email && (
                                  <div className="flex items-center gap-2">
                                    <Mail className="h-4 w-4" />
                                    {parte.email}
                                  </div>
                                )}
                                {parte.telefone && (
                                  <div className="flex items-center gap-2">
                                    <Phone className="h-4 w-4" />
                                    {parte.telefone}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResendLink(parte);
                                }}
                                title="Reenviar link do formulário"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => handleEditParte(e, parte)}
                                title="Editar parte"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteParte(parte.id);
                                }}
                                title="Excluir parte"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Contratos Tab */}
              {activeTab === 'contratos' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-foreground">Contratos</h3>
                    <Button size="sm" onClick={handleGenerateContrato}>
                      <Plus className="h-4 w-4 mr-2" />
                      Gerar Contrato
                    </Button>
                  </div>

                  {contratos.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Nenhum contrato gerado</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {contratos.map((contrato) => (
                        <div key={contrato.id} className="bg-card border border-border rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-foreground">
                                Contrato v{contrato.versao}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                Criado em {format(new Date(contrato.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(contrato.status)}`}>
                                {contrato.status}
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSelectedContratoId(contrato.id);
                                  setIsContratoViewerOpen(true);
                                }}
                              >
                                <FileCheck className="h-4 w-4 mr-2" />
                                Visualizar
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={async () => {
                                  if (!window.confirm('Tem certeza que deseja excluir este contrato? Esta ação não pode ser desfeita.')) {
                                    return;
                                  }

                                  try {
                                    const { error } = await supabase
                                      .from('contratos_gerados')
                                      .delete()
                                      .eq('id', contrato.id);

                                    if (error) throw error;

                                    toast({
                                      title: 'Sucesso',
                                      description: 'Contrato excluído com sucesso',
                                    });

                                    // Reload contratos
                                    loadData();
                                  } catch (error) {
                                    console.error('Erro ao excluir contrato:', error);
                                    toast({
                                      title: 'Erro',
                                      description: 'Não foi possível excluir o contrato',
                                      variant: 'destructive',
                                    });
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Anexos Tab */}
              {activeTab === 'anexos' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-foreground">Anexos</h3>
                    <Button size="sm" onClick={handleAddAnexo}>
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Anexo
                    </Button>
                  </div>

                  {anexos.length === 0 ? (
                    <div className="text-center py-8">
                      <Paperclip className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Nenhum anexo cadastrado</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {anexos.map((anexo) => (
                        <div key={anexo.id} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Paperclip className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="font-medium text-foreground">{anexo.nome_arquivo}</p>
                              <p className="text-sm text-muted-foreground">
                                {anexo.tipo} • {format(new Date(anexo.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownloadAnexo(anexo.url_arquivo, anexo.nome_arquivo)}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Baixar
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Histórico Tab */}
              {activeTab === 'historico' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground">Histórico do Processo</h3>

                  {historico.length === 0 ? (
                    <div className="text-center py-8">
                      <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Nenhum histórico disponível</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {historico.map((item) => (
                        <div key={item.id} className="flex gap-3 p-3 bg-card border border-border rounded-lg">
                          <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-foreground">{item.acao}</h4>
                              <span className="text-sm text-muted-foreground">
                                {format(new Date(item.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                              </span>
                            </div>
                            {item.descricao && (
                              <p className="text-sm text-muted-foreground mt-1">{item.descricao}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Modais */}
        <ParteForm
          isOpen={isParteFormOpen}
          onClose={handleParteFormClose}
          processoId={processo.id}
          processoTitulo={processo.titulo}
          tipoProcesso={processo.tipo}
          onSave={handleParteFormClose}
          parte={editingParte}
        />

        {viewingParte && (
          <ParteDetailModal
            isOpen={isParteDetailOpen}
            onClose={() => {
              setIsParteDetailOpen(false);
              setViewingParte(null);
            }}
            parte={viewingParte}
          />
        )}
        
        <AnexoUpload
          isOpen={isAnexoUploadOpen}
          onClose={handleAnexoUploadClose}
          processoId={processo.id}
          onUploadComplete={handleAnexoUploadClose}
        />

        <ContratoGenerator
          isOpen={isContratoGeneratorOpen}
          onClose={() => setIsContratoGeneratorOpen(false)}
          processoId={processo.id}
          tipoProcesso={processo.tipo}
          onGenerated={handleContratoGenerated}
        />

        {selectedContratoId && (
          <ContratoViewer
            isOpen={isContratoViewerOpen}
            onClose={() => {
              setIsContratoViewerOpen(false);
              setSelectedContratoId(null);
              loadData();
            }}
            contratoId={selectedContratoId}
            processoId={processo.id}
          />
        )}
      </div>
    </div>
  );
};